import { LoanParams } from 'app/models/loan-params'
import {
    Group,
    NumberInput,
    Select,
    SelectItem,
    Stack,
    Switch,
    Text,
} from '@mantine/core'
import CustomDatePicker from 'app/views/components/date-picker/date-picker-logic'
import PrimaryButton from 'app/views/components/buttons/PrimaryButton'
import {
    currencyOptions,
    dayBasisOptions,
    roundingOptions,
    pikOptions,
} from 'app/models/dropdown-options'
import FormWrapperWithSubtitle from '../common/form-wrapper-with-subtitle'
import { renderLoanAmountSubtitle } from '../common/loan-amount-subtitle-utils'
import {
    ErrorNotification,
    SuccessNotification,
} from 'app/views/components/notifications/notification'
import { useState, useEffect } from 'react'
import { FacilityParams } from 'app/models/facility-params'
import { useDispatch } from 'react-redux'
import { LoanConversionParams } from 'app/models/loan-conversion-params'
import {
    Option,
} from 'app/models/dropdown-options'
import { toNumberInputValue } from 'app/utils/util-functions'
import { submitForApproval } from 'app/state/ducks/loan-conversions/thunks'
import React from 'react'
import WarningText from 'app/views/components/warning-text/warning-text'
import { getPikConfig } from '../loans/utilities/pik-config-utils'
import { deriveAllInRate, formatRatePercent, rateDiffers } from 'app/utils/rate-rounding'


type Props = {
    form: any
    close: any
    facility: FacilityParams | undefined
    loan: LoanParams
    conversion: LoanConversionParams | undefined
    facilityindexOptions: Option[]
    createDraftConversion: () => Promise<any>
    onInterestBaseRateChange: (value: number | '') => void
    changeContractPeriod: (event: string | null) => void
    changeIndexOption: (event: string | null) => void
    changeStartDate: (event: any) => void
    facilityContractPeriodOptions: SelectItem[]
    disableEndDate: boolean
}

export default function ConversionFormPresentation({
    form,
    close,
    facility,
    loan,
    conversion,
    facilityindexOptions,
    facilityContractPeriodOptions,
    disableEndDate,
    createDraftConversion,
    onInterestBaseRateChange,
    changeContractPeriod,
    changeIndexOption,
    changeStartDate,

}: Props) {
    const dispatch = useDispatch()
    const [isLoading, setIsLoading] = useState(false)
    // A conversion keeps the loan's currency, so the rate is only relevant when
    // that currency differs from the facility's base currency.
    const isForeignCurrencyLoan =
        !!facility?.baseCurrency &&
        !!form.values.currency &&
        form.values.currency !== facility.baseCurrency

    const pikConfig = getPikConfig(facility?.pikOption)

    const onSubmit = async () => {
        setIsLoading(true)
        const result = await createDraftConversion()
        setIsLoading(false)
        if (result.success) {
            close()
        }
    }

    const saveAndSubmit = async () => {
        setIsLoading(true)
        const response: any = await createDraftConversion()
        try {
            if (!response) {
                return
            }
            const result: any = await dispatch(
                submitForApproval(response.payload.data)
            )
            if (result.success) {
                SuccessNotification({
                    title: 'Successful Conversion Submitted',
                    message: 'loan conversion submitted successfully',
                })
            } else {
                ErrorNotification({
                    title: 'Conversion created but not submitted.',
                    message: result.payload ?? 'Could not submit Loan Conversion',
                })
            }
        } finally {
            close()
            setIsLoading(false)
        }
    }

    // All-In Rate with PIK Premium for display: ROUNDED Base Rate + Margin + CAS + PIK Premium.
    // The rounded base rate is derived alongside so the two can never disagree.
    // The derived fields are dependencies too (guarded by `rateDiffers`) so a
    // saved draft re-hydrated after this effect gets corrected.
    const derivedRates = deriveAllInRate({
        interestBaseRate: form.values.interestBaseRate,
        rounding: form.values.rounding,
        margin: form.values.margin,
        casValue: form.values.casValue,
        pikPremium: form.values.pikPremium,
    })
    useEffect(() => {
        const { roundedBaseRate, allInRate } = derivedRates
        if (rateDiffers(form.values.interestBaseRateWithRounding, roundedBaseRate)) {
            form.setFieldValue('interestBaseRateWithRounding', roundedBaseRate)
        }
        if (rateDiffers(form.values.allInRate, allInRate)) {
            form.setFieldValue('allInRate', allInRate)
        }
        if (rateDiffers(form.values.allInRateWithPikPremium, allInRate)) {
            form.setFieldValue('allInRateWithPikPremium', allInRate)
        }
    }, [derivedRates.roundedBaseRate, derivedRates.allInRate, form.values.interestBaseRateWithRounding, form.values.allInRate, form.values.allInRateWithPikPremium])

    return (
        <FormWrapperWithSubtitle
            title={
                (conversion ? 'Edit' : 'Create') +
                ' Conversion' +
                (facility?.pikOption !== 'No_PIK' ? ' w/PIK' : '')
            }
            subtitle={renderLoanAmountSubtitle({
                existingAmount: Number(loan?.amount ?? 0),
                currency: loan?.currency ?? 'USD',
            })}
        >
            <div className="content">
                <form onSubmit={form.onSubmit(() => onSubmit())}>
                    <div className="create-new">
                        <Stack spacing="xl">
                            {conversion && conversion?.status !== 'Draft' ? (
                                <Text className="topFormErrorText">
                                    Conversion has already been submitted
                                </Text>
                            ) : null}

                            {pikConfig.showPikConfiguration && (
                                <Group noWrap>
                                    <Select
                                        searchable
                                        readOnly
                                        disabled
                                        label="PIK Option"
                                        w="100%"
                                        data={pikOptions}
                                        value={facility?.pikOption}
                                        styles={{
                                            input: {
                                                backgroundColor: '#f5f5f5',
                                                cursor: 'not-allowed',
                                            },
                                        }}
                                    />
                                    {pikConfig.showNewLoanPikToggle && (
                                        <div style={{ width: '100%' }}>
                                            <Stack style={{ marginTop: '15px' }}>
                                                <Group>
                                                    <Switch
                                                        labelPosition="left"
                                                        label="New Loan PIK (ON/OFF)"
                                                        size="lg"
                                                        checked={form.values.newLoanWithPik || false}
                                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                            form.setFieldValue('newLoanWithPik', event.currentTarget.checked)
                                                            if (!event.currentTarget.checked) {
                                                                form.setFieldValue('pikPremium', 0)
                                                                form.setFieldValue('pikInterestPercent', 0)
                                                                form.setFieldValue('cashInterestPercent', 0)
                                                            } else {
                                                                form.setFieldValue('pikPremium', loan?.pikPremium ? Number(loan.pikPremium) : 0)
                                                            }
                                                        }}
                                                    />
                                                </Group>
                                            </Stack>
                                        </div>
                                    )}
                                </Group>
                            )}

                            <Group noWrap>
                                <Select
                                    searchable
                                    withAsterisk
                                    label="New Index Rate"
                                    placeholder="Select Index Rate"
                                    w="100%"
                                    data={facilityindexOptions}
                                    {...form.getInputProps('indexOption')}
                                    onChange={e => {
                                        changeIndexOption(e)
                                    }}
                                />
                                <Select
                                    searchable
                                    withAsterisk
                                    readOnly
                                    data={currencyOptions}
                                    label="Select Currency"
                                    placeholder="Currency"
                                    w="100%"
                                    {...form.getInputProps('currency')}
                                />
                                {isForeignCurrencyLoan && (
                                    <NumberInput
                                        w="100%"
                                        precision={6}
                                        label="FX Rate"
                                        placeholder="Enter FX rate"
                                        description={`${form.values.currency} \u2192 ${facility?.baseCurrency}`}
                                        min={0}
                                        {...form.getInputProps('fxRate')}
                                        value={toNumberInputValue(form.values.fxRate)}
                                    />
                                )}

                                <NumberInput
                                    withAsterisk
                                    precision={2}
                                    w="100%"
                                    label="Amount"
                                    id="amount"
                                    placeholder="Enter Amount"
                                    parser={value =>
                                        value ? value.replace(/\$\s?|(,*)/g, '') : ''
                                    }
                                    formatter={value =>
                                        !Number.isNaN(parseFloat(value ?? ''))
                                            ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                                            : ''
                                    }
                                    {...form.getInputProps('amount')}
                                />
                            </Group>

                            <Stack>
                                {!disableEndDate && (
                                    <Select
                                        disabled={disableEndDate}
                                        searchable
                                        withAsterisk
                                        label="Contract Period"
                                        placeholder="Select Contract Period"
                                        w="100%"
                                        data={facilityContractPeriodOptions}
                                        {...form.getInputProps('contractPeriod')}
                                        onChange={e => {
                                            changeContractPeriod(e)
                                        }}
                                    />
                                )}
                                <Group noWrap>
                                    <CustomDatePicker
                                        required
                                        label={'Conversion Start Date'}
                                        date={form.values.startDate}
                                        setDate={(value: any) => {
                                            form.setFieldValue('startDate', value)
                                            changeStartDate(value)
                                        }}
                                        holidayCalendars={facility?.holidayCalendar ?? []}
                                    />

                                    <CustomDatePicker
                                        label={'Maturity Date'}
                                        disabled={disableEndDate}
                                        date={form.values.endDate}
                                        setDate={(value: any) =>
                                            form.setFieldValue('endDate', value)
                                        }
                                        holidayCalendars={facility?.holidayCalendar ?? []}
                                    />

                                    <Select
                                        searchable
                                        readOnly
                                        data={dayBasisOptions}
                                        label="Day basis"
                                        w="100%"
                                        {...form.getInputProps('dayBasis')}
                                    />
                                </Group>
                                {(form.isTouched('startDate') || form.isTouched('maturity')) &&
                                    form.values.maturity &&
                                    new Date(form.values.startDate) >=
                                    new Date(form.values.maturity) ? (
                                    <div className="topFormErrorText">
                                        Start Date Must Be Before Maturity Date
                                    </div>
                                ) : null}
                            </Stack>

                            <Group noWrap>
                                <NumberInput
                                    w="100%"
                                    precision={5}
                                    label="Interest Rate "
                                    parser={value =>
                                        value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
                                    }
                                    formatter={value =>
                                        !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
                                    }
                                    {...form.getInputProps('interestBaseRate')}
                                    onChange={onInterestBaseRateChange}
                                />

                                <NumberInput
                                    readOnly
                                    w="100%"
                                    precision={5}
                                    label="Interest Rate with Rounding"
                                    parser={value =>
                                        value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
                                    }
                                    formatter={value =>
                                        !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
                                    }
                                    {...form.getInputProps('interestBaseRateWithRounding')}
                                />

                                <Select
                                    searchable
                                    data={roundingOptions}
                                    readOnly
                                    label="Interest Base Rate Rounding"
                                    w="100%"
                                    {...form.getInputProps('rounding')}
                                />
                            </Group>

                            <Group noWrap>
                                <NumberInput
                                    w="100%"
                                    readOnly
                                    precision={5}
                                    label="Margin"
                                    parser={value =>
                                        value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
                                    }
                                    formatter={value =>
                                        !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
                                    }
                                    {...form.getInputProps('margin')}
                                />

                                {form.values.casValue !== undefined && form.values.casValue !== 0 && (
                                    <NumberInput
                                        readOnly
                                        w="100%"
                                        precision={5}
                                        label="CAS Rate"
                                        parser={value =>
                                            value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
                                        }
                                        formatter={formatRatePercent}
                                        {...form.getInputProps('casValue')}
                                    />
                                )}

                                <NumberInput
                                    readOnly
                                    className="applyGrayBackground"
                                    w="100%"
                                    precision={5}
                                    label="All In Rate"
                                    parser={value =>
                                        value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
                                    }
                                    // Shown as stored: margin, CAS and PIK premium are not rounded.
                                    formatter={value => (Number.isNaN(parseFloat(value ?? '0')) ? '0 %' : formatRatePercent(value))}
                                    {...form.getInputProps('allInRate')}
                                    value={form.values.allInRate ?? 0}
                                />
                            </Group>

                            {/* Show PIK configuration fields for PIK_Optional when toggle is ON, or always for PIK_Scheduled */}
                            {(pikConfig.showNewLoanPikFields ||
                                (pikConfig.showNewLoanPikFieldsWhenEnabled && form.values.newLoanWithPik)) && (
                                    <>
                                        <Group noWrap sx={{ marginBottom: '10px' }}>
                                            <NumberInput
                                                w="100%"
                                                precision={5}
                                                label="PIK Premium (%)"
                                                parser={value =>
                                                    value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
                                                }
                                                formatter={value =>
                                                    !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
                                                }
                                                {...form.getInputProps('pikPremium')}
                                            />
                                            <NumberInput
                                                withAsterisk
                                                w="100%"
                                                precision={5}
                                                label="PIK Interest (%)"
                                                parser={value =>
                                                    value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
                                                }
                                                formatter={value =>
                                                    !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
                                                }
                                                {...form.getInputProps('pikInterestPercent')}
                                            />
                                            <NumberInput
                                                withAsterisk
                                                w="100%"
                                                precision={5}
                                                label="Cash Interest (%)"
                                                parser={value =>
                                                    value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
                                                }
                                                formatter={value =>
                                                    !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
                                                }
                                                {...form.getInputProps('cashInterestPercent')}
                                            />
                                        </Group>

                                        {form.values.cashInterestPercent !== undefined &&
                                            form.values.pikInterestPercent !== undefined &&
                                            form.values.allInRate !== undefined &&
                                            (() => {
                                                const pikInterest = Number(form.values.pikInterestPercent) || 0
                                                const cashInterest = Number(form.values.cashInterestPercent) || 0
                                                // Use allInRateWithPikPremium for validation (the display rate WITH PIK Premium)
                                                const allInRateWithPik = Number(form.values.allInRateWithPikPremium) || Number(form.values.allInRate) || 0
                                                const sum = pikInterest + cashInterest
                                                const tolerance = 0.01
                                                const diff = Math.round(Math.abs(sum - allInRateWithPik) * 100) / 100
                                                return diff >= tolerance
                                            })() && (
                                                <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                                                    <WarningText
                                                        type="error"
                                                        text="PIK Cash % + PIK Interest % must equal All In Rate"
                                                    />
                                                </div>
                                            )}
                                    </>
                                )}

                            <Group noWrap mt={10}>
                                <PrimaryButton
                                    loading={isLoading}
                                    type="submit"
                                    disabled={!form.isValid()}
                                    text="Save"
                                    w="100%"
                                />
                                <PrimaryButton
                                    loading={isLoading}
                                    onClick={() => saveAndSubmit()}
                                    disabled={!form.isValid()}
                                    text="Create and Submit"
                                    w="100%"
                                />
                            </Group>
                        </Stack>
                    </div>
                </form>
            </div>
        </FormWrapperWithSubtitle>
    )
}
